import { useCallback } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import { useTranslation } from 'react-i18next'

import type { CustomerContractCsvExportRow } from '~/models/customerContractCsvExport'
import {
  buildCustomerContractCsvExport,
  toCustomerContractCsvExportRows,
} from '~/models/customerContractCsvExport'
import { DownloadModal } from '~/pages/AdminUploads/components/DownloadModal'
import { useExportCustomerContracts } from '~/pages/AdminUploads/hooks/useExportCustomerContracts'
import { commonUploadCsvFileName } from '~/pages/AdminUploads/models/downloadFileName'

type Props = {
  isOpen: boolean
  onClose: () => void
}

/**
 * 入稿済み顧客契約のダウンロード確認モーダル (uploads/common.md §6 / AC-DUC-028 / AC-DUC-032)。
 * 見込み・失注を含む全地点を 1 地点 1 行で出す。
 */
export const CustomerContractDownloadModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync: exportAsync } = useExportCustomerContracts()
  const typeName = t('page:adminUploads.types.customerContract.name')

  const exportRows = useCallback(async () => {
    const resp = await exportAsync({})
    return {
      rows: toCustomerContractCsvExportRows(resp.rows),
      dataUpdatedAt:
        resp.dataUpdatedAt !== undefined
          ? timestampDate(resp.dataUpdatedAt)
          : undefined,
    }
  }, [exportAsync])

  const fileName = useCallback(
    () => commonUploadCsvFileName(typeName),
    [typeName],
  )

  return (
    <DownloadModal<CustomerContractCsvExportRow>
      buildCsv={buildCustomerContractCsvExport}
      exportRows={exportRows}
      fileName={fileName}
      isOpen={isOpen}
      onClose={onClose}
      typeName={typeName}
    />
  )
}
